import { API_BASE_URL } from "./config";
import { readSessionToken } from "./session";
import type { AppNotification } from "./api/notifications";
import { parseSSEEvents } from "./chat-events";

export type NotificationEventPayload =
  | {
      type: "notification-created" | "notification-updated";
      notification: AppNotification;
      unread_count: number;
    }
  | {
      type: "notification-deleted";
      notification_id: string;
      unread_count: number;
    };

export function createNotificationEventSource(callbacks: {
  onEvent: (event: NotificationEventPayload) => void;
  onUnavailable?: () => void;
}): { close: () => void } {
  const controller = new AbortController();

  const connect = async () => {
    const token = readSessionToken();
    if (!token) {
      callbacks.onUnavailable?.();
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/notifications/events`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream"
        },
        signal: controller.signal
      });

      if (!response.ok || !response.body) {
        callbacks.onUnavailable?.();
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSSEEvents(buffer);
        buffer = parsed.remainder;

        for (const event of parsed.events) {
          if (
            event.type !== "notification-created" &&
            event.type !== "notification-updated" &&
            event.type !== "notification-deleted"
          ) {
            continue;
          }
          const data = JSON.parse(event.data) as NotificationEventPayload;
          callbacks.onEvent(data);
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      callbacks.onUnavailable?.();
    }
  };

  void connect();

  return {
    close: () => controller.abort()
  };
}
